import { useTheme } from '@mui/material';
import { DropdownChip } from 'sharedComponents/Chip/DropdownChip';
import { DropdownChipOption } from 'sharedComponents/Chip/types';
import { HiringRequestStatus } from 'utils/typedEnums';
import { useMutationUpdateHiringRequest } from '../../../api/referenceMutations';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { ATSConnectHiringRequestTestIds } from 'data-testids/ATS';
import auth from 'utils/auth';
import { Chip } from 'sharedComponents/Chip';
import { useState } from 'react';
import { HiringRequestStatusDropdownProps, SelectedDisplay } from '../../../types';

export const HiringRequestStatusDisplay: React.FC<HiringRequestStatusDropdownProps> = ({
  status,
  actionsComplete,
  hiringRequestId,
}) => {
  const theme = useTheme();
  const districtId = auth.getUser()?.profile?.district.id;
  const { mutate, isError, reset } = useMutationUpdateHiringRequest(districtId);
  const errorMessage = 'Something went wrong. Please refresh the page and try again.';

  const placeholderOption =
    status === HiringRequestStatus.PENDING && actionsComplete
      ? {
          label: 'Select status',
          value: 'select',
          colors: {
            background: theme.palette.white.main,
            text: theme.palette.black.main,
            border: theme.palette.black.main,
          },
        }
      : undefined;

  const dropdownOptions: DropdownChipOption<HiringRequestStatus>[] = [
    {
      label: 'Approved',
      value: HiringRequestStatus.APPROVED,
      colors: { background: theme.palette.primary.main, text: theme.palette.white.main },
    },
    {
      label: 'Rejected',
      value: HiringRequestStatus.REJECTED,
      colors: { background: theme.palette.error.main, text: theme.palette.white.main },
    },
  ];

  let defaultSelected: DropdownChipOption<HiringRequestStatus> | undefined = undefined;
  if (status !== HiringRequestStatus.PENDING) {
    defaultSelected = dropdownOptions.find((o) => o.value === status);
  }

  const [selectedOption, setSelectedOption] = useState<
    DropdownChipOption<HiringRequestStatus> | undefined
  >(defaultSelected);

  const handleSelect = (option: DropdownChipOption<HiringRequestStatus>) => {
    if (option.value !== status) {
      const previousOption = selectedOption;
      setSelectedOption(option);

      mutate(
        {
          hiringRequestId,
          payload: { status: option.value },
        },
        {
          onError: () => {
            setSelectedOption(previousOption);
          },
        }
      );
    }
  };

  if (auth.isDistrictAdmin() && actionsComplete && status === HiringRequestStatus.PENDING) {
    return (
      <>
        <ErrorSnackbar
          message={errorMessage}
          onClose={reset}
          open={isError}
          dataTestId={ATSConnectHiringRequestTestIds.STATUS_ERROR_SNACKBAR}
        />
        <DropdownChip
          options={dropdownOptions}
          handleSelect={handleSelect}
          placeholderOption={placeholderOption}
          dataTestId={ATSConnectHiringRequestTestIds.STATUS_DROPDOWN}
          selected={selectedOption}
        />
      </>
    );
  }

  let selectedDisplay: SelectedDisplay = selectedOption;
  if (!selectedDisplay) {
    selectedDisplay = {
      label: 'Pending',
      colors: {
        background: theme.palette.white.main,
        text: theme.palette.black.main,
        border: theme.palette.gray.darker,
      },
    };
  }

  return (
    <Chip
      label={selectedDisplay.label}
      sx={{
        width: '133px',
        height: theme.spacing(4),
        backgroundColor: selectedDisplay.colors.background,
        color: selectedDisplay.colors.text,
        border: selectedDisplay.colors.border
          ? `2px solid ${selectedDisplay.colors.border}`
          : 'none',
        cursor: 'default',
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.body2.fontSize,
      }}
      dataTestId={ATSConnectHiringRequestTestIds.STATUS_CHIP}
    />
  );
};
